import Workspace from 'iam/models/Workspace'
import { observable, action, computed, makeObservable } from 'mobx'
import { Observable } from 'rxjs'
import Member from 'iam/models/Member'
import { map, tap, filter } from 'rxjs/operators'
import DocumentsListViewModel from './DocumentsList/DocumentsListViewModel'
import { UserDocumentsViewModelAdapter } from '../UserDocumentsViewModelAdapter'
import { AlertState } from 'alerts/Alert'
import { task } from 'mobx-task'
import UploadBarViewModel from './UploadBar/UploadBarViewModel'
import { ViewDocumentDetailsDelegate } from './DocumentDetailsRootViewModel'
import { Id } from 'types/definitions'
import orderBy from 'lodash/orderBy'
import WorkspaceSelectorState from 'iam/views/AuthenticationContainer/WorkspaceSelector/WorkspaceSelectorState'

/**
 * Workspace filter for Documents.
 */
export type DocumentsWorkspaceFilter = AllWorkspaces | SpecificWorkspace

/**
 * Documents in all workspaces.
 */
export interface AllWorkspaces {
  type: 'AllWorkspaces'
}

/**
 * Documents in a specific workspace.
 */
export interface SpecificWorkspace {
  type: 'SpecificWorkspace'
  workspace: Workspace
}

export default class DocumentRootViewModel {
  @observable workspaceFilter: DocumentsWorkspaceFilter = {
    type: 'AllWorkspaces',
  }

  documentsListVM = new DocumentsListViewModel(
    this.adapter,
    () => this.workspaceForFiltering,
  )

  uploadBarVM = new UploadBarViewModel(
    this.adapter.newAlert.bind(this.adapter),
    this.adapter.documentActionsAdapter.uploadNewDocument.bind(
      this.adapter.documentActionsAdapter,
    ),
  )

  constructor(
    public adapter: UserDocumentsViewModelAdapter,
    public viewDocumentDetails: ViewDocumentDetailsDelegate,
    currentMember$: Observable<Member>,
    private workspaceSelectorState?: WorkspaceSelectorState,
  ) {
    makeObservable(this)
    // Syncs the workspace filter when the current member changes.
    // For documents we default to the current workspace view in order to prevent
    // accidentally modifying documents in another workspace when coming
    // from another workspace
    //
    // When current member changes...
    currentMember$
      .pipe(
        // ...and we're coming from another workspace or we were previously looking at
        // all workspaces (this includes the default workspaceFilter value)...
        filter((member) => {
          // If isDefaultToAllWorkspaces is enabled, don't auto-switch to member's workspace
          // This ensures Documents stays on "All workspaces" (the default initial value)
          if (this.workspaceSelectorState?.isDefaultToAllWorkspaces) {
            return false
          }
          return (
            this.workspaceFilter.type === 'AllWorkspaces' ||
            this.workspaceFilter.workspace !== member.workspace
          )
        }),
        // ...get the workspace from currentMember$...
        map((x) => x.workspace),
        // ...and set as the workspace filter
        tap(this.selectWorkspaceForFiltering),
      )
      .subscribe()
  }

  activate() {
    //
  }

  @computed
  get currentWorkspace() {
    return this.adapter.getCurrentMember().workspace
  }

  @computed
  get isWorkspaceSelected() {
    return !!this.workspaceForFiltering
  }

  /**
   * Current user workspaces.
   */
  @computed
  get currentUserWorkspaces() {
    return orderBy(
      this.adapter.getCurrentUserWorkspaceMemberships().map((m) => m.workspace),
      (ws) => ws.name.toLowerCase(),
      'asc',
    )
  }

  @computed
  get workspaceForFiltering() {
    return this.workspaceFilter.type === 'SpecificWorkspace'
      ? this.workspaceFilter.workspace
      : undefined
  }

  /**
   * Sets the workspace filter the the
   * current member's context workspace
   */
  @action.bound
  selectCurrentWorkspaceForFiltering() {
    this.selectWorkspaceForFiltering(this.currentWorkspace)
  }

  /**
   * Handler for file buttons
   * @param files
   */
  @action.bound
  onQueueFilesUpload(files: FileList) {
    if (!this.uploadBarVM.uploading) {
      this.uploadFiles(files)
    }
  }

  /**
   * Upload files to the currently selected workspace
   * @param files
   */
  @action
  async uploadFiles(files: FileList) {
    if (files.length < 1) {
      return
    }

    if (!this.workspaceForFiltering) {
      this.adapter
        .newAlert()
        .withState(AlertState.Warning)
        .saying(
          'Issue Uploading Documents',
          'You must select a workspace to upload documents to',
        )
        .autoDismiss()
        .withActions({
          title: `Switch to ${this.currentWorkspace.name}`,
          handler: () => this.selectCurrentWorkspaceForFiltering(),
        })

      return
    }

    const workspaceId = Id.assert(this.workspaceForFiltering.id)

    const filesToUpload = Array.from(files).map((f) => ({
      file: f,
      options: {
        workspace_id: workspaceId,
      },
    }))

    await this.uploadBarVM.uploadFiles(filesToUpload)
  }

  @computed
  get workspaceDocs() {
    const workspace = this.workspaceForFiltering
    if (!workspace) {
      return this.adapter.documents
    }

    return this.adapter.documents.filter((doc) => doc.workspace === workspace)
  }

  @computed
  get hasDocuments() {
    return this.workspaceDocs.length > 0
  }

  @computed
  get isWorkspaceEmpty() {
    return this.isWorkspaceSelected && !this.hasDocuments
  }

  /**
   * Selects the workspace to filter by.
   */
  @action.bound
  selectWorkspaceForFiltering(workspace: Workspace) {
    this.workspaceFilter =
      workspace === Workspace.none
        ? { type: 'AllWorkspaces' }
        : { workspace, type: 'SpecificWorkspace' }

    this.fetchDocs()
  }

  /**
   * Fetcher task for the pages's tags and documents
   * This is here because we want to show the page
   * shell but start fetching relevant documents
   * right away
   */
  fetchDocs = task.resolved(async () => {
    await this.adapter.fetchTags()
    await this.getScrollProvider.fetch()
  })

  /**
   * Getter for the relevant scroll provider based on
   * the workspace selector filter.
   *
   * This is also passed down to the document list
   * so that it can pass it to the InfiniteScrollLoader
   *
   */
  @computed
  get getScrollProvider() {
    const workspaceId = this.workspaceForFiltering?.id
    if (workspaceId) {
      return this.adapter.getWorkspaceDocsScrollProvider(workspaceId)
    } else {
      return this.adapter.getUserDocsScrollProvider()
    }
  }
}
